import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  AssignmentTurnedIn,
  CallMade,
  Home,
  LocalHospital,
  Settings,
  TrendingUp,
} from "@material-ui/icons";
import type { ComponentType } from "react";
import {
  linkToEpocusHome,
  linkToEpocusMe,
  linkToEpocusProgram,
  linkToEpocusReferralsInbox,
  linkToEpocusReview,
  linkToEpocusSettings,
} from "../links";
import type { Capability } from "./rbac/capabilities";
import { useCan } from "./rbac/use-can";

/**
 * The capabilities the nav actually gates on — a subset of the full table, kept
 * in lockstep with the grant lookups in `useVisibleNavItems`. Deriving from
 * `Capability` keeps it honest: renaming a capability breaks here too. Gating a
 * nav item on a capability outside this set is a compile error, not a silent
 * runtime hide.
 */
type NavCapability = Extract<
  Capability,
  "viewReviewQueue" | "viewReferrals" | "viewProgramDashboard"
>;

export type NavItemDef = {
  /** Full label shown in the desktop sidebar. */
  label: string;
  /** Compact label shown in the mobile bottom nav. */
  shortLabel: string;
  path: () => string;
  icon: ComponentType;
  badgeKey: "review" | "referral" | null;
  /**
   * Capability required to see this item. Omit to make the item visible to
   * every ePOCUS role (Home, My Performance, Settings).
   */
  capability?: NavCapability;
  /** Feature flag required to see this item, applied on top of any capability. */
  featureFlag?: "referrals";
};

export const NAV_ITEMS: NavItemDef[] = [
  {
    label: "Home",
    shortLabel: "Home",
    path: linkToEpocusHome,
    icon: Home,
    badgeKey: null,
  },
  {
    label: "Review Queue",
    shortLabel: "Review",
    path: linkToEpocusReview,
    icon: AssignmentTurnedIn,
    badgeKey: "review",
    capability: "viewReviewQueue",
  },
  {
    label: "Referrals",
    shortLabel: "Referrals",
    path: linkToEpocusReferralsInbox,
    icon: CallMade,
    badgeKey: "referral",
    capability: "viewReferrals",
    featureFlag: "referrals",
  },
  {
    label: "My Performance",
    shortLabel: "Me",
    path: linkToEpocusMe,
    icon: TrendingUp,
    badgeKey: null,
  },
  {
    label: "Program Dashboard",
    shortLabel: "Program",
    path: linkToEpocusProgram,
    icon: LocalHospital,
    badgeKey: null,
    capability: "viewProgramDashboard",
  },
  {
    label: "Settings",
    shortLabel: "Settings",
    path: linkToEpocusSettings,
    icon: Settings,
    badgeKey: null,
  },
];

/**
 * The nav items the current user is allowed to see, filtered through `useCan`
 * per the canonical capability table. Both the sidebar and the bottom nav
 * consume this so desktop and mobile stay in lockstep.
 *
 * `grants` is exhaustive over `NavCapability`, so a newly gated nav item can't
 * compile without a matching lookup here.
 */
export const useVisibleNavItems = (): NavItemDef[] => {
  const grants: Record<NavCapability, boolean> = {
    viewReviewQueue: useCan("viewReviewQueue"),
    viewReferrals: useCan("viewReferrals"),
    viewProgramDashboard: useCan("viewProgramDashboard"),
  };

  const flagGrants: Record<NonNullable<NavItemDef["featureFlag"]>, boolean> = {
    referrals: useFeatureFlag(featureFlags.epocus.referrals)[0],
  };

  return NAV_ITEMS.filter(({ capability, featureFlag }) => {
    if (featureFlag && !flagGrants[featureFlag]) return false;
    return capability === undefined || grants[capability];
  });
};
